import React, { useEffect, useState } from 'react';
import { Loan } from '../types';
import { motion, AnimatePresence } from 'framer-motion';
import { X, AlertTriangle, UserCheck, ShieldAlert } from 'lucide-react';
interface TakeOverModalProps {
  loan: Loan | null;
  onClose: () => void;
  onConfirm: (loanId: string) => void;
}
export function TakeOverModal({
  loan,
  onClose,
  onConfirm
}: TakeOverModalProps) {
  const [acknowledged, setAcknowledged] = useState(false);
  // Reset acknowledgement whenever the modal targets a new loan
  useEffect(() => {
    setAcknowledged(false);
  }, [loan?.id]);
  return <AnimatePresence data-id="element-228">
      {loan && <div className="fixed inset-0 z-50 flex items-center justify-center p-6" data-id="element-229">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} data-id="element-230" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.96,
        y: 12
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.96,
        y: 12
      }} transition={{
        type: 'spring',
        damping: 24,
        stiffness: 240
      }} className="relative w-full max-w-[520px] bg-white rounded-2xl shadow-2xl overflow-hidden" data-id="element-231">
            {/* Header */}
            <div className="px-6 pt-6 pb-4 flex items-start gap-4" data-id="element-232">
              <div className="w-11 h-11 rounded-full bg-amber-50 border border-amber-100 flex items-center justify-center shrink-0" data-id="element-233">
                <UserCheck size={20} className="text-amber-600" data-id="element-234" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-235">
                <h2 className="text-lg font-semibold text-slate-900" data-id="element-236">
                  Take over this loan?
                </h2>
                <p className="text-sm text-slate-500 mt-0.5" data-id="element-237">
                  You'll be reassigned as Loan Officer on{' '}
                  <span className="font-medium text-slate-700" data-id="element-238">
                    {loan.name}
                  </span>{' '}
                  — {loan.borrowerName}.
                </p>
              </div>
              <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-full transition-colors" data-id="element-239">
                <X size={16} data-id="element-240" />
              </button>
            </div>

            {/* Responsibilities */}
            <div className="px-6 pb-5" data-id="element-241">
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3" data-id="element-242">
                <div className="flex items-start gap-3" data-id="element-243">
                  <div className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-[11px] font-semibold shrink-0 mt-0.5" data-id="element-244">
                    1
                  </div>
                  <p className="text-sm text-slate-700 leading-relaxed" data-id="element-245">
                    You will be reassigned as the{' '}
                    <span className="font-semibold text-slate-900" data-id="element-246">
                      Loan Officer
                    </span>{' '}
                    of record on this opportunity.
                  </p>
                </div>
                <div className="flex items-start gap-3" data-id="element-247">
                  <div className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-[11px] font-semibold shrink-0 mt-0.5" data-id="element-248">
                    2
                  </div>
                  <p className="text-sm text-slate-700 leading-relaxed" data-id="element-249">
                    You are responsible for{' '}
                    <span className="font-semibold text-slate-900" data-id="element-250">
                      finishing the loan
                    </span>{' '}
                    through funding.
                  </p>
                </div>
                <div className="flex items-start gap-3" data-id="element-251">
                  <div className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-[11px] font-semibold shrink-0 mt-0.5" data-id="element-252">
                    3
                  </div>
                  <p className="text-sm text-slate-700 leading-relaxed" data-id="element-253">
                    If the borrower stops engaging, you must{' '}
                    <span className="font-semibold text-slate-900" data-id="element-254">
                      withdraw the loan
                    </span>{' '}
                    with a documented reason.
                  </p>
                </div>
              </div>

              {/* Warning callout */}
              <div className="mt-4 flex items-start gap-2 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-lg" data-id="element-255">
                <ShieldAlert size={16} className="text-amber-600 shrink-0 mt-0.5" data-id="element-256" />
                <p className="text-xs text-amber-800 leading-relaxed" data-id="element-257">
                  This action is logged and notifies the previous Loan Officer.
                  Take-overs are reviewed for misuse.
                </p>
              </div>

              {/* Acknowledgement */}
              <label className="mt-4 flex items-start gap-2.5 cursor-pointer select-none group" data-id="element-258">
                <input type="checkbox" checked={acknowledged} onChange={e => setAcknowledged(e.target.checked)} className="mt-0.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500" data-id="element-259" />
                <span className="text-sm text-slate-700 group-hover:text-slate-900" data-id="element-260">
                  I understand and accept ownership of this loan.
                </span>
              </label>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-end gap-3" data-id="element-261">
              <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200 rounded-lg transition-colors" data-id="element-262">
                Cancel
              </button>
              <button disabled={!acknowledged} onClick={() => onConfirm(loan.id)} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-600 flex items-center gap-2" data-id="element-263">
                <UserCheck size={14} data-id="element-264" />
                Confirm Take Over
              </button>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}